import { gql, useMutation } from '@apollo/client';
import classNames from 'classnames';
import Quill from 'quill/core';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import Button from '../components/Button';
import LoadingIndicator from '../components/LoadingIndicator';
import SVGIcon from '../components/SVGIcon';
import type {
  EditorPlaceholder_PostFragment,
  EditorPlaceholderImportFilesMutation,
  EditorPlaceholderImportFilesMutationVariables,
} from '../graphql/generated/graphqlTypes';
import googleDriveIcon from '../icons/googleDrive.svg';
import postIcon from '../icons/post.svg';
import templateIcon from '../icons/template.svg';
import uploadFileIcon from '../icons/uploadFile.svg';
import { useAddTopicToPost } from '../post/sharing/postPermissionOperations';
import useDeletePost from '../post/useDeletePost';
import GoogleDrivePicker from '../search/GoogleDrivePicker';
import { StatefulTemplatesModal } from '../templates/TemplatesModal';
import UIActions from '../ui/UIActions';
import postPath from '../utils/url/postPath';
import { useRouter } from '../utils/router/v3Bridge';
import styles from './EditorPlaceholder.module.scss';
import { useQuill } from './QuillContext';

const EDITOR_PLACEHOLDER_POST = gql`
  fragment EditorPlaceholder_post on Post {
    id
    topics {
      id
    }
  }
`;

const IMPORT_FILES_MUTATION = gql`
  mutation EditorPlaceholderImportFiles($files: [Upload]!) {
    importFiles(files: $files, isDraft: true) {
      post {
        id
        publishedAt
        title
      }
    }
  }
`;

interface EditorPlaceholderProps {
  post: EditorPlaceholder_PostFragment;
}

const EditorPlaceholder = ({ post }: EditorPlaceholderProps) => {
  const { quill } = useQuill();
  const dispatch = useDispatch();
  const [isHidden, setIsHidden] = useState(false);

  const router = useRouter();

  const deletePost = useDeletePost();
  const [importFiles] = useMutation<
    EditorPlaceholderImportFilesMutation,
    EditorPlaceholderImportFilesMutationVariables
  >(IMPORT_FILES_MUTATION);

  const addTopic = useAddTopicToPost();
  const copyTopics = async (targetPostId: string) => {
    const topicIds = post.topics.map((topic) => topic.id);
    await Promise.all(
      topicIds.map((topicId) => addTopic(targetPostId, topicId)),
    );
  };

  if (isHidden || !quill) {
    return null;
  }

  const startLoading = () => {
    dispatch(
      UIActions.openModal(() => <LoadingIndicator delay={0} card />, {
        disableOutsideClose: true,
      }),
    );
  };

  const stopLoading = () => {
    dispatch(UIActions.closeModal());
  };

  const deletePostWithLoading = async () => {
    startLoading();
    try {
      await deletePost(post.id);
    } finally {
      stopLoading();
    }
  };

  return (
    <div
      data-no-print
      className={classNames(styles.container, 'editor-placeholder')}
    >
      <div className={styles.content}>
        Start typing or choose a starting point from below
        <div className={styles.actions}>
          <Button
            className={styles.button}
            onClick={() => {
              setIsHidden(true);

              let targetIndex = 0;
              const [firstLine] = quill.getLine(0);
              if (!firstLine) return;
              if (!quill.noTitleLine) {
                targetIndex =
                  // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                  firstLine.offset(quill.scroll) + firstLine.length();
              }
              quill.focusWithoutScrolling();
              quill.setSelection(targetIndex, 0, Quill.sources.SILENT);
            }}
          >
            <SVGIcon className={styles.icon}>{postIcon}</SVGIcon>
            Start from scratch
          </Button>
          <Button
            className={styles.button}
            onClick={() => {
              dispatch(
                UIActions.openModal(StatefulTemplatesModal, {
                  onCreatePost: deletePostWithLoading,
                }),
              );
            }}
          >
            <SVGIcon className={styles.icon}>{templateIcon}</SVGIcon>
            Use a template
          </Button>
          <Button className={styles.button}>
            <SVGIcon className={styles.icon}>{uploadFileIcon}</SVGIcon>
            <label className={styles.fileInputLabel}>
              <input
                type="file"
                accept=".md,.docx,.html,.htm"
                className={styles.fileInput}
                onChange={async (e) => {
                  const file = e.currentTarget.files?.[0];
                  if (!file) return;

                  startLoading();
                  try {
                    const { data } = await importFiles({
                      variables: { files: [file] },
                    });
                    const importedPost = data?.importFiles[0]?.post;
                    if (importedPost) {
                      await copyTopics(importedPost.id);
                      router.replace(postPath(importedPost));
                      await deletePost(post.id);
                    }
                  } finally {
                    stopLoading();
                  }
                }}
              />
            </label>
            Upload a file
          </Button>
          <Button
            className={styles.button}
            onClick={() => {
              dispatch(
                UIActions.openModal(GoogleDrivePicker, {
                  disableOutsideClose: true,
                  navigationMethod: 'replace',
                  onBeforeNavigate: deletePostWithLoading,
                  topicIds: post.topics.map((topic) => topic.id),
                }),
              );
            }}
          >
            <SVGIcon className={styles.icon}>{googleDriveIcon}</SVGIcon>
            Import from Google
          </Button>
        </div>
      </div>
    </div>
  );
};

EditorPlaceholder.fragments = {
  post: EDITOR_PLACEHOLDER_POST,
};

export default EditorPlaceholder;
